/**
 * Date formatting utility functions
 * Note: Server uses America/New_York timezone
 */

/**
 * Server timezone constant
 */
export const SERVER_TIMEZONE = 'America/New_York';

/**
 * Convert YYYY-MM-DD to MM/DD/YYYY for display
 * @param dateStr - Date string in YYYY-MM-DD format or MM/DD/YYYY format
 * @returns Formatted date string in MM/DD/YYYY format
 */
export const formatDateForDisplay = (dateStr: string | null | undefined): string => {
  // Convert YYYY-MM-DD to MM/DD/YYYY for display
  if (!dateStr) return new Date().toLocaleDateString('en-US');
  if (dateStr.includes('-') && dateStr.length === 10) {
    const [year, month, day] = dateStr.split('-');
    return `${month}/${day}/${year}`;
  }
  return dateStr;
};

/**
 * Convert MM/DD/YYYY to YYYY-MM-DD for HTML date input
 * @param dateStr - Date string in MM/DD/YYYY format or YYYY-MM-DD format
 * @returns Formatted date string in YYYY-MM-DD format
 */
export const formatDateForInput = (dateStr: string): string => {
  // Convert MM/DD/YYYY to YYYY-MM-DD for HTML date input
  if (!dateStr) {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  }
  const parts = dateStr.split('/');
  if (parts.length === 3) {
    return `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
  }
  // If already in YYYY-MM-DD format, return as is
  if (dateStr.includes('-') && dateStr.length === 10) {
    return dateStr;
  }
  return dateStr;
};

/**
 * Format a date string or Date object to a readable date format
 * Handles ISO strings, timestamps, and Date objects
 * @param dateInput - Date string (ISO, timestamp, or date string) or Date object
 * @returns Formatted date string like "Dec 31, 2024"
 */
export const formatDate = (dateInput: string | Date | null | undefined): string => {
  if (!dateInput) return '-';
  
  try {
    let date: Date;
    
    if (dateInput instanceof Date) {
      date = dateInput;
    } else if (typeof dateInput === 'string') {
      // Handle ISO strings, timestamps, and date strings
      date = new Date(dateInput);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.warn('Invalid date:', dateInput);
        return '-';
      }
    } else {
      return '-';
    }
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch (error) {
    console.error('Error formatting date:', error, dateInput);
    return '-';
  }
};

/**
 * Format a time string or Date object to a readable time format
 * Handles ISO strings, timestamps, and Date objects
 * @param timeInput - Time string (ISO, timestamp) or Date object
 * @returns Formatted time string like "10:30:45 AM"
 */
export const formatTime = (timeInput: string | Date | null | undefined): string => {
  if (!timeInput) return '-';
  
  try {
    let date: Date;
    
    if (timeInput instanceof Date) {
      date = timeInput;
    } else if (typeof timeInput === 'string') {
      // Handle ISO strings and timestamps
      date = new Date(timeInput);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.warn('Invalid time:', timeInput);
        return '-';
      }
    } else {
      return '-';
    }
    
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  } catch (error) {
    console.error('Error formatting time:', error, timeInput);
    return '-';
  }
};

/**
 * Format a Date object to YYYY-MM-DD string in local timezone (not UTC)
 * This prevents timezone issues where toISOString() can shift dates by one day
 * @param date - Date object to format
 * @returns Formatted date string in YYYY-MM-DD format
 */
export const formatDateLocal = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Format a Date object to YYYY-MM-DD string in server timezone (New York)
 * Use this when sending dates to the server to ensure consistency
 * @param date - Date object to format
 * @returns Formatted date string in YYYY-MM-DD format in NY timezone
 */
export const formatDateToServerTimezone = (date: Date): string => {
  // Convert date to NY timezone for formatting
  const nyDate = new Date(date.toLocaleString('en-US', { timeZone: SERVER_TIMEZONE }));
  const year = nyDate.getFullYear();
  const month = String(nyDate.getMonth() + 1).padStart(2, '0');
  const day = String(nyDate.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Parse a date string from server (assumed to be in NY timezone context)
 * and return a Date object. For date-only strings (YYYY-MM-DD), creates
 * a date at midnight in the user's local timezone.
 * @param dateStr - Date string from server (YYYY-MM-DD or ISO string)
 * @returns Date object
 */
export const parseServerDate = (dateStr: string | null | undefined): Date | null => {
  if (!dateStr) return null;
  
  // If it's a date-only string (YYYY-MM-DD), parse it as local date
  // to avoid timezone shifts
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  
  // For ISO strings with time, parse normally
  // The server sends timestamps in NY timezone context
  return new Date(dateStr);
};

/**
 * Format relative time (e.g., "2 hours ago") in New York timezone
 * This ensures accurate relative time calculations regardless of browser timezone.
 * The server sends timestamps in UTC, but we want to display relative time as if
 * both the notification time and current time are in NY timezone.
 * @param dateString - ISO date string from server (UTC)
 * @returns Formatted relative time string
 */
export const formatRelativeTimeInNY = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  
  try {
    // Parse the date string (it's in UTC from the database)
    const date = new Date(dateString);
    
    if (isNaN(date.getTime())) {
      return '-';
    }
    
    // Get current time
    const now = new Date();
    
    // Get the time components in NY timezone for both dates
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: SERVER_TIMEZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
    
    // Helper to extract values from formatted parts
    const getValue = (parts: Intl.DateTimeFormatPart[], type: string) => 
      parseInt(parts.find(p => p.type === type)?.value || '0', 10);
    
    // Get formatted parts for both dates in NY timezone
    const nowParts = formatter.formatToParts(now);
    const dateParts = formatter.formatToParts(date);
    
    // Extract all time components in NY timezone
    const nowYear = getValue(nowParts, 'year');
    const nowMonth = getValue(nowParts, 'month');
    const nowDay = getValue(nowParts, 'day');
    const nowHour = getValue(nowParts, 'hour');
    const nowMinute = getValue(nowParts, 'minute');
    const nowSecond = getValue(nowParts, 'second');
    
    const dateYear = getValue(dateParts, 'year');
    const dateMonth = getValue(dateParts, 'month');
    const dateDay = getValue(dateParts, 'day');
    const dateHour = getValue(dateParts, 'hour');
    const dateMinute = getValue(dateParts, 'minute');
    const dateSecond = getValue(dateParts, 'second');
    
    // Calculate the difference by converting both to a common reference (Unix timestamp in NY timezone)
    // We'll create date strings and parse them treating NY timezone as if it were UTC for comparison
    // This works because we're only interested in the difference, not absolute times
    const nowNYTimestamp = Date.UTC(nowYear, nowMonth - 1, nowDay, nowHour, nowMinute, nowSecond);
    const dateNYTimestamp = Date.UTC(dateYear, dateMonth - 1, dateDay, dateHour, dateMinute, dateSecond);
    
    // Calculate the difference in milliseconds
    const diffMs = nowNYTimestamp - dateNYTimestamp;
    
    // Handle negative differences (future dates)
    if (diffMs < 0) {
      return 'just now';
    }
    
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);
    
    // Format the relative time
    if (diffSeconds < 60) {
      return 'just now';
    } else if (diffMinutes < 60) {
      return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    } else if (diffWeeks < 4) {
      return `${diffWeeks} week${diffWeeks !== 1 ? 's' : ''} ago`;
    } else if (diffMonths < 12) {
      return `${diffMonths} month${diffMonths !== 1 ? 's' : ''} ago`;
    } else {
      return `${diffYears} year${diffYears !== 1 ? 's' : ''} ago`;
    }
  } catch (error) {
    console.error('Error formatting relative time:', error, dateString);
    return '-';
  }
};
